import { useState, useCallback, useRef } from 'react';
import apiClient from 'utils/ApiClient';
import { LogEntry, LOG_PAGE_SIZE, MAX_LIVE_ENTRIES, LineFilter } from '../types';
import { normalizeLevelName, buildSelectorString } from '../utils';

interface UseLogQueryOptions {
  selectedServer: string;
  selectedLabels: Record<string, string[]>;
  lineFilter: LineFilter | null;
  getTimeRangeMs: () => { start: number; end: number };
}

export function useLogQuery({ selectedServer, selectedLabels, lineFilter, getTimeRangeMs }: UseLogQueryOptions) {
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [logQueryLoading, setLogQueryLoading] = useState(false);
  const [logQueryError, setLogQueryError] = useState<string | null>(null);
  const [logQueryDone, setLogQueryDone] = useState(false);
  const [commonLabels, setCommonLabels] = useState<Record<string, string>>({});
  const [logStats, setLogStats] = useState<{ totalLines: number; streams: number }>({ totalLines: 0, streams: 0 });
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  const [loadingMoreLogs, setLoadingMoreLogs] = useState(false);
  const [liveReloading, setLiveReloading] = useState(false);
  const nextEntryIdRef = useRef(0);
  const liveReloadingRef = useRef(false);
  const logEntriesRef = useRef(logEntries);
  logEntriesRef.current = logEntries;
  const lastCoverageEndMsRef = useRef<number>(0);

  const parseStreams = useCallback((
    streams: { stream: Record<string, string>; values: [string, string][] }[],
    existingKeys?: Set<string>,
  ): { entries: LogEntry[]; totalValueCount: number } => {
    const entries: LogEntry[] = [];
    let totalValueCount = 0;
    for (const s of streams) {
      const level = normalizeLevelName(s.stream.level || s.stream.severity || '');
      totalValueCount += s.values.length;
      for (const [tsNs, line] of s.values) {
        if (existingKeys) {
          const key = `${Number(tsNs)}:${line.slice(0, 80)}`;
          if (existingKeys.has(key)) continue;
        }
        entries.push({
          timestamp: Number(tsNs),
          line,
          level,
          labels: s.stream,
          id: nextEntryIdRef.current++,
        });
      }
    }
    return { entries, totalValueCount };
  }, []);

  const handleRunQuery = useCallback(async (
    resetCallbacks: {
      setIsLive: (v: boolean) => void;
      setExpandedLogRows: (v: Set<number>) => void;
      setAllRowsExpanded: (v: boolean) => void;
      setChartZoomRange: (v: null) => void;
    },
  ) => {
    if (!selectedServer) return;
    if (!Object.values(selectedLabels).some(v => v.length > 0)) return;

    const query = buildSelectorString(selectedLabels, lineFilter);

    try {
      resetCallbacks.setIsLive(false);
      setLogQueryLoading(true);
      setLogQueryError(null);
      setLogQueryDone(false);
      resetCallbacks.setExpandedLogRows(new Set());
      resetCallbacks.setAllRowsExpanded(false);
      resetCallbacks.setChartZoomRange(null);
      nextEntryIdRef.current = 0;

      const { start, end } = getTimeRangeMs();
      const response = await apiClient.post(`/il/logs/loki-query-range/${selectedServer}`, {
        query, start, end, limit: LOG_PAGE_SIZE, direction: 'backward',
      });

      const data = response.data;
      const streams: { stream: Record<string, string>; values: [string, string][] }[] = data?.result || [];
      const { entries, totalValueCount } = parseStreams(streams);
      entries.sort((a, b) => b.timestamp - a.timestamp);

      if (streams.length > 0) {
        const first = { ...streams[0].stream };
        for (let i = 1; i < streams.length; i++) {
          for (const key of Object.keys(first)) {
            if (streams[i].stream[key] !== first[key]) {
              delete first[key];
            }
          }
        }
        setCommonLabels(first);
      } else {
        setCommonLabels({});
      }

      lastCoverageEndMsRef.current = end;
      setLogEntries(entries);
      setLogStats({ totalLines: entries.length, streams: streams.length });
      setHasMoreLogs(totalValueCount >= LOG_PAGE_SIZE);
      setLogQueryDone(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch logs';
      setLogQueryError(msg);
      setLogEntries([]);
      setHasMoreLogs(false);
    } finally {
      setLogQueryLoading(false);
    }
  }, [selectedServer, selectedLabels, lineFilter, getTimeRangeMs, parseStreams]);

  const loadMoreLogs = useCallback(async () => {
    if (loadingMoreLogs || !hasMoreLogs || !selectedServer || logEntries.length === 0) return;
    if (!Object.values(selectedLabels).some(v => v.length > 0)) return;

    const query = buildSelectorString(selectedLabels, lineFilter);

    const spinnerMinDelay = new Promise((r) => setTimeout(r, 400));

    try {
      setLoadingMoreLogs(true);

      const oldestEntry = logEntries[logEntries.length - 1];
      const endMs = Math.floor(oldestEntry.timestamp / 1_000_000);
      const { start } = getTimeRangeMs();

      const [response] = await Promise.all([
        apiClient.post(`/il/logs/loki-query-range/${selectedServer}`, {
          query, start, end: endMs, limit: LOG_PAGE_SIZE, direction: 'backward',
        }),
        spinnerMinDelay,
      ]);

      const data = response.data;
      const streams: { stream: Record<string, string>; values: [string, string][] }[] = data?.result || [];
      const existingKeys = new Set(logEntries.map((e) => `${e.timestamp}:${e.line.slice(0, 80)}`));
      const { entries: newEntries, totalValueCount } = parseStreams(streams, existingKeys);
      newEntries.sort((a, b) => b.timestamp - a.timestamp);

      if (newEntries.length > 0) {
        setLogEntries((prev) => [...prev, ...newEntries]);
        setLogStats((prev) => ({
          totalLines: prev.totalLines + newEntries.length,
          streams: prev.streams,
        }));
      }

      setHasMoreLogs(totalValueCount >= LOG_PAGE_SIZE && newEntries.length > 0);
    } catch (err: any) {
      console.error('Failed to load more logs:', err);
      setHasMoreLogs(false);
    } finally {
      setLoadingMoreLogs(false);
    }
  }, [loadingMoreLogs, hasMoreLogs, selectedServer, selectedLabels, lineFilter, logEntries, getTimeRangeMs, parseStreams]);

  const [loadingAllLogs, setLoadingAllLogs] = useState(false);
  const cancelLoadAllRef = useRef(false);

  const loadAllLogs = useCallback(async () => {
    if (loadingAllLogs || !hasMoreLogs || !selectedServer || logEntries.length === 0) return;
    if (!Object.values(selectedLabels).some(v => v.length > 0)) return;

    const query = buildSelectorString(selectedLabels, lineFilter);

    try {
      cancelLoadAllRef.current = false;
      setLoadingAllLogs(true);
      const { start } = getTimeRangeMs();
      let currentEntries = logEntriesRef.current;
      let moreAvailable = true;

      while (moreAvailable) {
        // Stop requested by the user — keep what we've fetched so far and leave
        // `hasMoreLogs` truthy so the remaining pages can still be loaded later.
        if (cancelLoadAllRef.current) break;

        const oldestEntry = currentEntries[currentEntries.length - 1];
        const endMs = Math.floor(oldestEntry.timestamp / 1_000_000);

        const response = await apiClient.post(`/il/logs/loki-query-range/${selectedServer}`, {
          query, start, end: endMs, limit: LOG_PAGE_SIZE, direction: 'backward',
        });

        const data = response.data;
        const streams: { stream: Record<string, string>; values: [string, string][] }[] = data?.result || [];
        const existingKeys = new Set(currentEntries.map((e) => `${e.timestamp}:${e.line.slice(0, 80)}`));
        const { entries: newEntries, totalValueCount } = parseStreams(streams, existingKeys);
        newEntries.sort((a, b) => b.timestamp - a.timestamp);

        if (newEntries.length > 0) {
          currentEntries = [...currentEntries, ...newEntries];
          setLogEntries(currentEntries);
          setLogStats((prev) => ({
            totalLines: prev.totalLines + newEntries.length,
            streams: prev.streams,
          }));
        }

        moreAvailable = totalValueCount >= LOG_PAGE_SIZE && newEntries.length > 0;
      }

      // `moreAvailable` is false only when Loki returned the last page; if the
      // loop exited because of a cancel it stays true so the user can resume.
      setHasMoreLogs(moreAvailable);
    } catch (err: any) {
      console.error('Failed to load all logs:', err);
    } finally {
      cancelLoadAllRef.current = false;
      setLoadingAllLogs(false);
    }
  }, [loadingAllLogs, hasMoreLogs, selectedServer, selectedLabels, lineFilter, logEntries, getTimeRangeMs, parseStreams]);

  const stopLoadAllLogs = useCallback(() => {
    cancelLoadAllRef.current = true;
  }, []);

  const reset = useCallback(() => {
    setLogEntries([]);
    setLogQueryLoading(false);
    setLogQueryError(null);
    setLogQueryDone(false);
    setCommonLabels({});
    setLogStats({ totalLines: 0, streams: 0 });
    setHasMoreLogs(false);
    setLoadingMoreLogs(false);
    setLoadingAllLogs(false);
    cancelLoadAllRef.current = false;
    setLiveReloading(false);
    liveReloadingRef.current = false;
    nextEntryIdRef.current = 0;
    lastCoverageEndMsRef.current = 0;
  }, []);

  const fetchNewerLogs = useCallback(async () => {
    if (!selectedServer || liveReloadingRef.current) return;
    if (!Object.values(selectedLabels).some(v => v.length > 0)) return;

    const query = buildSelectorString(selectedLabels, lineFilter);

    try {
      liveReloadingRef.current = true;
      setLiveReloading(true);

      const endMs = Date.now();
      let startMs: number;
      if (lastCoverageEndMsRef.current > 0) {
        startMs = lastCoverageEndMsRef.current;
      } else {
        const currentEntries = logEntriesRef.current;
        const newestTs = currentEntries.length > 0 ? currentEntries[0].timestamp : 0;
        startMs = newestTs > 0 ? Math.ceil(newestTs / 1_000_000) : endMs - 30_000;
      }

      const response = await apiClient.post(`/il/logs/loki-query-range/${selectedServer}`, {
        query, start: startMs, end: endMs, limit: LOG_PAGE_SIZE, direction: 'backward',
      });

      lastCoverageEndMsRef.current = endMs;

      const data = response.data;
      const streams: { stream: Record<string, string>; values: [string, string][] }[] = data?.result || [];

      const currentEntries = logEntriesRef.current;
      const existingKeys = new Set(
        currentEntries.slice(0, 200).map((e) => `${e.timestamp}:${e.line.slice(0, 80)}`)
      );
      const { entries: newEntries } = parseStreams(streams, existingKeys);

      if (newEntries.length > 0) {
        newEntries.sort((a, b) => b.timestamp - a.timestamp);
        setLogEntries((prev) => {
          const merged = [...newEntries, ...prev];
          return merged.length > MAX_LIVE_ENTRIES ? merged.slice(0, MAX_LIVE_ENTRIES) : merged;
        });
        setLogStats((prev) => ({
          totalLines: prev.totalLines + newEntries.length,
          streams: prev.streams,
        }));
      }
    } catch (err: any) {
      console.error('Live reload failed:', err);
    } finally {
      liveReloadingRef.current = false;
      setLiveReloading(false);
    }
  }, [selectedServer, selectedLabels, lineFilter, parseStreams]);

  return {
    logEntries,
    logQueryLoading,
    logQueryError,
    setLogQueryError,
    logQueryDone,
    commonLabels,
    logStats,
    hasMoreLogs,
    loadingMoreLogs,
    loadingAllLogs,
    liveReloading,
    handleRunQuery,
    loadMoreLogs,
    loadAllLogs,
    stopLoadAllLogs,
    fetchNewerLogs,
    reset,
  };
}
